import { Button, Dialog } from '@protoss/ui';
import * as React from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * DirtyStateContext - centralized dirty-state registry for unsaved-changes guarding.
 *
 * Every form or editable surface registers its dirty state via a unique key.
 * The context handles all side effects centrally:
 *   - Single `useBlocker` for React Router in-app navigation blocking
 *   - Single `beforeunload` listener for browser refresh / close / external nav
 *   - Exposes `isAnyDirty` for page-title dirty markers
 *   - Exposes `hasDirtyInScope(prefix)` for scoped checks (e.g. right-rail panels)
 *   - Exposes `confirmIfDirty()` for non-route actions (e.g. closing the right rail)
 *
 * Dirty keys follow a naming convention:
 *   - Main-content forms: auto-generated or descriptive (e.g. 'domain-form')
 *   - Right-rail forms: prefixed with 'rr:' (e.g. 'rr:integration-settings')
 *
 * The provider must live inside the React Router data router (so useBlocker works)
 * but above all route components. It is mounted in AppLayout (App.tsx).
 */

interface DirtyStateContextValue {
  /** Register or update a dirty source. Call whenever isDirty changes. */
  register: (key: string, isDirty: boolean, label?: string) => void;
  /** Unregister a dirty source (typically on unmount). */
  unregister: (key: string) => void;
  /** Whether ANY registered source is dirty. */
  isAnyDirty: boolean;
  /**
   * Returns true if any registered key starting with the given prefix is dirty.
   * Use for scoped dirty checks (e.g. right-rail panels: prefix 'rr:').
   */
  hasDirtyInScope: (prefix: string) => boolean;
  /**
   * Show a custom confirm dialog if any source matching the optional prefix is dirty.
   * Returns a Promise that resolves to true if the user confirms discard (or nothing is dirty).
   */
  confirmIfDirty: (prefix?: string) => Promise<boolean>;
}

interface PendingConfirm {
  /** Human-readable labels of the dirty forms in scope - shown as a list in the dialog. */
  labels: string[];
  resolve: (confirmed: boolean) => void;
}

// In dev, store the context object on window so it survives HMR re-evaluation.
// React Fast Refresh overrides hot.decline(), meaning this module gets
// re-executed on every hot update. Without a stable reference the new module's
// context object differs from the one the still-mounted DirtyStateProvider was
// created with, causing "must be used within DirtyStateProvider" errors.
const CTX_KEY = '__protoss_dirty_state_ctx__' as const;
type CtxType = React.Context<DirtyStateContextValue | null>;

function getStableContext(): CtxType {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const w = window as unknown as Record<string, unknown>;
    if (!w[CTX_KEY]) {
      w[CTX_KEY] = React.createContext<DirtyStateContextValue | null>(null);
    }
    return w[CTX_KEY] as CtxType;
  }
  return React.createContext<DirtyStateContextValue | null>(null);
}

const DirtyStateContext = getStableContext();

export function DirtyStateProvider({ children }: { children: React.ReactNode }) {
  const dirtyMapRef = React.useRef(new Map<string, boolean>());
  const labelMapRef = React.useRef(new Map<string, string>());
  const [isAnyDirty, setIsAnyDirty] = React.useState(false);
  const [pendingConfirm, setPendingConfirm] = React.useState<PendingConfirm | null>(null);

  const computeIsAnyDirty = React.useCallback(() => {
    for (const v of dirtyMapRef.current.values()) {
      if (v) {
        return true;
      }
    }
    return false;
  }, []);

  const recompute = React.useCallback(() => {
    setIsAnyDirty(computeIsAnyDirty());
  }, [computeIsAnyDirty]);

  const register = React.useCallback(
    (key: string, isDirty: boolean, label?: string) => {
      if (label !== undefined) labelMapRef.current.set(key, label);
      if (dirtyMapRef.current.get(key) !== isDirty) {
        dirtyMapRef.current.set(key, isDirty);
        recompute();
      }
    },
    [recompute]
  );

  const unregister = React.useCallback(
    (key: string) => {
      labelMapRef.current.delete(key);
      if (dirtyMapRef.current.has(key)) {
        dirtyMapRef.current.delete(key);
        recompute();
      }
    },
    [recompute]
  );

  /** Collect human-readable labels for all dirty sources matching the optional prefix. */
  const getDirtyLabels = React.useCallback((prefix?: string): string[] => {
    const result: string[] = [];
    for (const [k, v] of dirtyMapRef.current) {
      if (v && (!prefix || k.startsWith(prefix))) {
        const label = labelMapRef.current.get(k);
        if (label) result.push(label);
      }
    }
    return result;
  }, []);

  const hasDirtyInScope = React.useCallback((prefix: string) => {
    for (const [k, v] of dirtyMapRef.current) {
      if (v && k.startsWith(prefix)) return true;
    }
    return false;
  }, []);

  const confirmIfDirty = React.useCallback(
    (prefix?: string): Promise<boolean> => {
      let dirty = false;
      if (prefix) {
        for (const [k, v] of dirtyMapRef.current) {
          if (v && k.startsWith(prefix)) {
            dirty = true;
            break;
          }
        }
      } else {
        for (const v of dirtyMapRef.current.values()) {
          if (v) {
            dirty = true;
            break;
          }
        }
      }
      if (!dirty) return Promise.resolve(true);

      return new Promise<boolean>((resolve) => {
        setPendingConfirm({
          labels: getDirtyLabels(prefix),
          resolve: (confirmed: boolean) => {
            if (confirmed) {
              // Clear matching keys immediately in the ref so beforeunload
              // is disarmed synchronously - no async gap.
              for (const k of dirtyMapRef.current.keys()) {
                if (!prefix || k.startsWith(prefix)) {
                  dirtyMapRef.current.set(k, false);
                }
              }
              recompute();
            }
            setPendingConfirm(null);
            resolve(confirmed);
          },
        });
      });
    },
    [getDirtyLabels, recompute]
  );

  // ── Single React Router blocker ─────────────────────────────────────────
  const blocker = useBlocker(React.useCallback(() => computeIsAnyDirty(), [computeIsAnyDirty]));

  React.useEffect(() => {
    if (blocker.state !== 'blocked') return;
    setPendingConfirm({
      labels: getDirtyLabels(),
      resolve: (confirmed) => {
        setPendingConfirm(null);
        if (confirmed) blocker.proceed();
        else blocker.reset();
      },
    });
  }, [blocker, getDirtyLabels]);

  // ── Single beforeunload handler ─────────────────────────────────────────
  // Installed once. Reads dirtyMapRef directly (always current) so it is
  // never stale even during the async gap between a confirmIfDirty resolve
  // and the React state update + effect cleanup propagating.
  React.useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent): void {
      if (!computeIsAnyDirty()) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [computeIsAnyDirty]);

  const value = React.useMemo<DirtyStateContextValue>(
    () => ({ register, unregister, isAnyDirty, hasDirtyInScope, confirmIfDirty }),
    // getDirtyLabels intentionally omitted - stable (no reactive deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [register, unregister, isAnyDirty, hasDirtyInScope, confirmIfDirty]
  );

  return (
    <DirtyStateContext.Provider value={value}>
      {children}
      {pendingConfirm && (
        <Dialog.Root open>
          <Dialog.Portal>
            <Dialog.Overlay className="dialog-overlay" />
            <Dialog.Content
              className="dialog-content dirty-confirm"
              aria-describedby="dirty-confirm-desc"
              onEscapeKeyDown={() => pendingConfirm.resolve(false)}
              onInteractOutside={(e) => e.preventDefault()}
            >
              <Dialog.Title className="dialog-title">Unsaved changes</Dialog.Title>
              <div id="dirty-confirm-desc" className="dirty-confirm__body">
                {pendingConfirm.labels.length > 0 ? (
                  <ul className="dirty-confirm__labels">
                    {pendingConfirm.labels.map((label) => (
                      <li key={label} className="dirty-confirm__label-item">
                        {label}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <Dialog.Description className="dirty-confirm__message">
                  Your unsaved changes will be discarded and cannot be recovered.
                </Dialog.Description>
              </div>
              <div className="dirty-confirm__actions">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => pendingConfirm.resolve(false)}
                >
                  Keep editing
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => pendingConfirm.resolve(true)}
                >
                  Discard changes
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </DirtyStateContext.Provider>
  );
}

/**
 * useDirtyStateContext - access the centralized dirty-state registry.
 *
 * Throws if called outside DirtyStateProvider. All hooks in the app that
 * need dirty-state awareness should go through this context.
 */
export function useDirtyStateContext(): DirtyStateContextValue {
  const ctx = React.useContext(DirtyStateContext);
  if (!ctx) throw new Error('useDirtyStateContext must be used within DirtyStateProvider');
  return ctx;
}
